import React, { useEffect, useState } from 'react';
import '../../css/MyAccount.css';
import { getAuth } from 'firebase/auth';
import { 
    getFirestore, 
    doc, 
    getDoc, 
    updateDoc, 
    collection, 
    getDocs, 
    orderBy, 
    query 
} from 'firebase/firestore';

function MyAccount() {
    const auth = getAuth();
    const firestore = getFirestore();

    const [userData, setUserData] = useState(null);
    const [orders, setOrders] = useState([]);   // ✅ state for orders
    const [isEditing, setIsEditing] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null); // ✅ state for clicked order

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        postalCode: ''
    });

    // ✅ Fetch user details + orders
    useEffect(() => {
        const fetchUserData = async () => {
            const user = auth.currentUser;
            if (user) {
                const userDoc = await getDoc(doc(firestore, 'users', user.uid));
                if (userDoc.exists()) {
                    const data = userDoc.data();
                    setUserData(data);
                    setFormData({
                        firstName: data.firstName || '',
                        lastName: data.lastName || '',
                        email: data.email || '',
                        phone: data.phone || '',
                        address1: data.address1 || '',
                        address2: data.address2 || '',
                        city: data.city || '',
                        state: data.state || '',
                        postalCode: data.postalCode || ''
                    });
                }

                // ✅ Fetch user orders
                const ordersRef = collection(firestore, 'users', user.uid, 'orders');
                const q = query(ordersRef, orderBy('createdAt', 'desc'));
                const snapshot = await getDocs(q);
                const orderList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setOrders(orderList);
            }
        };
        fetchUserData();
    }, [auth, firestore]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSave = async () => {
        const user = auth.currentUser;
        if (user) {
            try {
                await updateDoc(doc(firestore, 'users', user.uid), formData);
                setUserData(formData);
                setIsEditing(false);
                alert("Profile updated successfully!");
            } catch (err) {
                console.error("Error updating profile:", err);
                alert("Failed to update profile.");
            }
        }
    };

    if (!userData) return <p>Loading...</p>;

    return (
        <div className="my-account">
            <h1>My Account</h1>

            {/* Account Information */}
            <div className="account-section">
                <h2>👤 Account Information</h2>
                <p><strong>First Name:</strong> {userData.firstName}</p>
                <p><strong>Last Name:</strong> {userData.lastName}</p>
                <p><strong>Email:</strong> {userData.email}</p>
                <p><strong>Phone:</strong> {userData.phone}</p>
                <p><strong>Address:</strong> {userData.address1} {userData.address2}</p>
                <p><strong>City:</strong> {userData.city}</p>
                <p><strong>State:</strong> {userData.state}</p>
                <p><strong>Postal Code:</strong> {userData.postalCode}</p>
                <button className='button' onClick={() => setIsEditing(true)}>Edit Profile</button>
            </div>

            {/* Orders */}
            {/* <div className="account-section">
                <h2>🛒 My Orders</h2>
                <ul className="order-list">
                    {orders.length === 0 ? (
                        <li>No orders found.</li>
                    ) : (
                        orders.map(order => (
                            <li 
                                key={order.id} 
                                onClick={() => setSelectedOrder(order)} // ✅ open popup on click
                                style={{ cursor: "pointer" }}
                            >
                                <strong>{order.orderId}</strong> - 
                                {order.items.length} Items - 
                                LKR {order.total?.toLocaleString('en-LK')} - 
                                <span className={`status ${order.status}`}>{order.status}</span>
                            </li>
                        ))
                    )}
                </ul>
            </div> */}

            {/* ✅ Order Details Popup */}
            {selectedOrder && (
                <div className="modal">
                    <div className="modal-content">
                        <h2>Order Details</h2>
                        <p><strong>Order ID:</strong> {selectedOrder.orderId}</p>
                        <p><strong>Status:</strong> {selectedOrder.status}</p>
                        <p><strong>Total:</strong> LKR {selectedOrder.total?.toLocaleString('en-LK')}</p>
                        <p><strong>Shipping:</strong> LKR {selectedOrder.shipping?.toLocaleString('en-LK')}</p>
                        <p><strong>Customer:</strong> {selectedOrder.customer?.firstName} {selectedOrder.customer?.lastName}</p>
                        <p><strong>Address:</strong> {selectedOrder.customer?.address1}, {selectedOrder.customer?.address2}, {selectedOrder.customer?.city}</p>
                        
                        <h3>Items</h3>
                        <ul>
                            {selectedOrder.items.map((item, idx) => (
                                <li key={idx}>
                                    {item.title} × {item.quantity} — Rs.{(item.price * item.quantity).toLocaleString('en-LK')}
                                </li>
                            ))}
                        </ul>

                        <div className="modal-actions">
                            <button className="button cancel" onClick={() => setSelectedOrder(null)}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Profile Modal */}
            {isEditing && (
                <div className="modal">
                    <div className="modal-content">
                        <h2>Edit Profile</h2>

                        <div className="form-row">
                            <div className="form-group">
                                <label>First Name</label>
                                <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} />
                            </div>
                            <div className="form-group">
                                <label>Last Name</label>
                                <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} />
                            </div>
                        </div>

                        <label>Email</label>
                        <input type="email" name="email" value={formData.email} onChange={handleChange} />

                        <label>Phone</label>
                        <input type="text" name="phone" value={formData.phone} onChange={handleChange} />

                        <label>Address 1</label>
                        <input type="text" name="address1" value={formData.address1} onChange={handleChange} />

                        <label>Address 2</label>
                        <input type="text" name="address2" value={formData.address2} onChange={handleChange} />

                        <div className="form-row">
                            <div className="form-group">
                                <label>City</label>
                                <input type="text" name="city" value={formData.city} onChange={handleChange} />
                            </div>
                            <div className="form-group">
                                <label>State</label>
                                <input type="text" name="state" value={formData.state} onChange={handleChange} />
                            </div>
                            <div className="form-group">
                                <label>Postal Code</label>
                                <input type="text" name="postalCode" value={formData.postalCode} onChange={handleChange} />
                            </div>
                        </div>

                        <div className="modal-actions">
                            <button className="button" onClick={handleSave}>Save</button>
                            <button className="button cancel" onClick={() => setIsEditing(false)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}

export default MyAccount;
